import { ComponentTemplate } from "src/lib/types/config";
import { CompassChartStyle } from "../charts/Compass";
import { CircleStyle, ItemStyle, LineStyle, LiveChartStyle } from "../charts/LiveChart";
import { AssetMap, Compass, LiveChart, TrackMap } from "./components";
import { specifyResponsiveStyles } from "./style";
// Components configuration templates
// Common props:
// source, sources - key of source in source list (see base.js)
// field, XField, yField, timeField - field in source data, see sources.cols
// params, chartStyle - params and style for charts, for details
//  see charts in Coremarine Frontend Library. Params are typically
//  title and axes labels


const circleStyle = (radius: number): CircleStyle => ({
    type: "circle",
    color: [
        "rgba(255, 10, 10, 1)",
        "rgba(255, 255, 10, .9)",
        "rgba(10, 255, 10, .7)",
        "rgba(10, 200, 240, .5)",
        "rgba(10, 10, 255, .3)"
    ],
    radius
});

export const circlesNormal = [circleStyle(4)];
export const circlesBig = [circleStyle(6)];
export const circlesVeryBig = [circleStyle(10)];


const lineStyle = (width: number): LineStyle => ({
    type: "line",
    color: "rgb(40, 120, 180)",
    width
});

const lineNormal = [lineStyle(2.5)];
const lineThick = [lineStyle(5)];
const lineVeryThick = [lineStyle(9)];


export const timeseriesResponsiveStyle = (
    itemsNormal: ItemStyle[],
    itemsSmall: ItemStyle[],
) =>
    specifyResponsiveStyles<LiveChartStyle>(
        {
            items: itemsNormal,
            m: { l: 80, t: 50, b: 50, r: 30 },
            label: {t: 30},
            lastValue: {t: 30, r: 30},
            crosshair: {t: 30, l: 70,}
        },
        {
            items: itemsSmall,
            m: {l: 80, t: 20, b: 35, r: 20},
            lastValue: {t: 5, r: 20},
            crosshair: {t: 5, l: 70}
        },
    );


const templates: {
    [key: string]: ComponentTemplate
} = {
    compass: Compass({
        chartStyle: {
            canvasBgColor: "rgb(23, 22, 51)",
            m: {t: 30, b: 30, l: 0, r: 0},
            responsive: specifyResponsiveStyles<CompassChartStyle>(
                {
                    m: {t: 0, b: 50, l: 0, r: 0},
                    fontSize: {minor: 20, major: 36}
                }, {
                    m: {t: 0, b: 20, l: 0, r: 0},
                    fontSize: {minor: 12, major: 20}
                }
            )
        }
    }),
    bullseye: LiveChart({
        chartClass: "BullseyeChart",
        chartStyle: {
            canvasBgColor: "rgb(23, 22, 51)",
            items: circlesNormal,
            // Title position
            label: {b: 30, l: -10, t: 30},
            // Margin
            m: { t: 80, l: 30, r: 35, b: 40 },
            // The coordinates of the point under cursor
            // in graph coordinate system
            // (Crosshair is crossed lines in cursor position)
            crosshair: {
                // Position
                r: 20, b: 10,
                format: {
                    // Two functions for x and y coordinates:
                    // x: x => x
                    // y: y => y
                }
            },
            // X axis style
            x: {
                ticks: 8,
                // number of secondary ticks between two primary ticks
                secondaryTicks: 2,
            },
            // Y axis style
            y: {
                ticks: 8,
                secondaryTicks: 2,
            },
            // Last value position and format.
            lastValue: {
                t: 62, r: 20,
            },
            fillArea: [{
                fill: "	#2dc93755",
                stroke: {
                    width: 3, color: "#e7b416"
                }
            }, {
                fill: "#db7b2b55",
                stroke: {
                    width: 3, color: "#cc3232"
                }
            }],
            responsive: specifyResponsiveStyles<LiveChartStyle>(
                {
                    label: {b: 30, l: -10, t: 30},
                    m: { t: 80, l: 30, r: 35, b: 40 },
                    lastValue: {t: 62},
                    items: circlesBig
                },
                {
                    label: {b: 30, l: -10, t: 30},
                    m: {t: 40, r: 15, l: 15},
                    lastValue: {t: 22},
                    items: circlesNormal
                },
            ),
        }
    }),
    processed: LiveChart({
        chartClass: "LiveScatterChart",
        chartStyle: {
            canvasBgColor: "rgb(23, 22, 51)",
            items: circlesNormal,
            x: {
                range: [0, 6e4],
                format: v => `${Math.round(v / 100) / 10}`
            },
            y: {
                secondaryTicks: 1,
                ticks: 5,
            },
            responsive: specifyResponsiveStyles<LiveChartStyle>(
                {
                    items: circlesNormal,
                    m: { t: 50, l: 70, r: 30, b: 60 },
                    lastValue: {t: 30, r: 30},
                    crosshair: {t: 55, r: 35},
                    x: {ticks: 5}
                },
                {
                    m: {t: 20, l: 70, b: 25, r: 20},
                    lastValue: {t: 5, r: 20},
                    crosshair: {t: 25, r: 20},
                    items: circlesNormal,
                    x: {ticks: 4}
                },
            ),
            // Filled polygon drawn on the graph, to highlight some
            // range of values
            fillArea: {
                fill:   "rgba(30, 230, 220, .2)",
                line: {
                    stroke: "rgba(10, 180, 120, .5)",
                    width: 2,
                },
                circle: {
                    fill:   "rgb(10, 180, 120)",
                    radius: 4
                }
            },
        }
    }),
    timeseries: LiveChart({
        chartClass: "LiveTimeChart",
        chartStyle: {
            canvasBgColor: "rgb(23, 22, 51)",
            x: { ticks: 12, secondaryTicks: 4 },
            y: { ticks: 6, secondaryTicks: 4 },
            items: [{
                type: "line",
                color: "rgb(40, 120, 180)",
                width: 2.5,
            }, {}],
            decimateValues: [
                {minPeriod: 1800000, interval: 5000},
                {minPeriod: 86400000, interval: 60000},
            ],
            responsive: timeseriesResponsiveStyle(
                lineNormal,
                lineNormal,
                lineThick,
                lineVeryThick,
            ),
        }
    }),
    timeseriesSlow: LiveChart({
        interval: 1000,
        chartClass: "LiveTimeChart",
        chartStyle: {
            canvasBgColor: "rgb(23, 22, 51)",
            items: [{
                type: "line",
                color: "rgb(40, 120, 180)",
                width: 2.5,
                point: {
                    display: true,
                    color: "#54DDA6",
                    radius: 2.5
                }
            }],
            x: { ticks: 12, secondaryTicks: 4 },
            y: {
                range: [-180, 180],
                unit: "deg",
                ticks: 6, secondaryTicks: 4
            },
            m: { t: 50, l: 70, r: 20, b: 70 },
            responsive: timeseriesResponsiveStyle(
                lineNormal,
                lineNormal,
                lineThick,
                lineVeryThick
            ),
        }
    }),
    period: LiveChart({
        chartClass: "LiveTimeChart",
        params: {
            title: "Period",
        },
        chartStyle: {
            canvasBgColor: "rgb(23, 22, 51)",
            items: circlesBig,
            x: { ticks: 12, secondaryTicks: 1 },
            y: {
                ticks: 6,
                secondaryTicks: 1,
                range: [0, Infinity],
                format: v => `${Math.round(v / 100) / 10}`
            },
            responsive: specifyResponsiveStyles<LiveChartStyle>(
               {
                    items: circlesNormal,
                    m: { t: 50, l: 70, r: 20, b: 40 },
                    lastValue: {t: 35, r: 20},
                    crosshair: {t: 35, l: 70,},
                },
                {
                    items: circlesNormal,
                    m: {t: 20, b: 10},
                    lastValue: {t: 5, r: 20},
                    crosshair: {t: 5, l: 70},
                    x: {format: () => " "},
                }
            ),
        }
    }),
    amplitude: LiveChart({
        chartClass: "LiveTimeChart",
        params: {
            title: "Amplitude",
        },
        chartStyle: {
            canvasBgColor: "rgb(23, 22, 51)",
            items: circlesNormal,
            m: { t: 50, l: 70, r: 20, b: 20 },
            x: { ticks: 12, secondaryTicks: 1 },
            y: {ticks: 6, secondaryTicks: 1},
            responsive: specifyResponsiveStyles<LiveChartStyle>(
                {
                    items: circlesNormal,
                    m: { t: 50, l: 70, r: 20, b: 40 },
                    lastValue: {t: 35, r: 20},
                    crosshair: {t: 35, l: 70,},
                },
                {
                    items: circlesNormal,
                    m: {t: 20, b: 10},
                    lastValue: {t: 5, r: 20},
                    crosshair: {t: 5, l: 70},
                    x: {format: () => " "},
                },
            ),
        },
    }),
    asset_map: AssetMap(),
    track_map: TrackMap(),
};

export default templates;